import { useCallback, useEffect, useRef, useState } from 'react';
import { createEars } from './ears';
import { useSharedAudio } from './sharedAudio';
import { errorText } from './voiceStrings';

/**
 * HOLD TO RECORD (the engine behind <VoiceButton>). begin() on press, release() on lift, lock() on slide up, cancel() on
 * slide toward the start edge or the trash, send() for a locked note. A note shorter than MIN_NOTE_MS is a small shake
 * (`shake` counts up so the button can replay it), never an error. The words go to onSend(text, language).
 *
 *   const r = useHoldToRecord({ language, transcribe, getLiveSession, onSend, haptic })
 *   r.phase  'idle' | 'recording' | 'locked' | 'sending'
 *   r.ms  r.level  r.bars (last ~28 levels, for the waveform)  r.words  r.error  r.shake
 */
export const MIN_NOTE_MS = 600;
const BARS = 28;
export const SENDING_TIMEOUT_MS = 20_000; // a note never waits longer than this for its words

export function useHoldToRecord({ language = 'en', transcribe, getLiveSession, onSend, haptic, deps = {} } = {}) {
  useSharedAudio();
  const [phase, setPhase] = useState('idle');
  const [ms, setMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [bars, setBars] = useState([]);
  const [words, setWords] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(0);
  const ears = useRef(null);
  const phaseRef = useRef('idle');
  const startedAt = useRef(0);
  const clock = useRef(null);
  const run = useRef(0);
  const lastBar = useRef(0);
  const props = useRef({});
  props.current = { transcribe, getLiveSession, onSend, haptic, language };
  const to = useCallback((p) => { phaseRef.current = p; setPhase(p); }, []);

  const stopClock = () => { if (clock.current) { clearInterval(clock.current); clock.current = null; } };
  const reset = useCallback(() => { stopClock(); setMs(0); setLevel(0); setBars([]); setWords(''); }, []);
  useEffect(() => () => { stopClock(); run.current += 1; try { ears.current?.close(); } catch (_) { /* gone */ } }, []);

  // THE PRESS OPENS THE EARS (iOS gives the microphone only inside the gesture): arm() is called from the pointerdown
  // handler itself and starts the ears at once; begin() — the hold confirmed at 220 ms — only shows the recording. A press
  // that turns out to be a tap is disarm()ed: the ears go, silently, and the live token was never asked for.
  const armed = useRef(false);
  const gate = useRef(null);

  const arm = useCallback(() => {
    if (phaseRef.current !== 'idle' || armed.current) return;
    const mine = (run.current += 1);
    armed.current = true;
    setError(''); reset();
    startedAt.current = Date.now();
    let open; let shut;
    const held = new Promise((res, rej) => { open = res; shut = rej; });
    held.catch(() => {});
    gate.current = { open, shut };
    ears.current = (deps.startEars || createEars)({
      kind: 'note', continuous: false,
      // The token is minted only for a hold, never for a tap.
      getLiveSession: props.current.getLiveSession ? () => held.then(() => props.current.getLiveSession()) : undefined,
      transcribe: (b) => props.current.transcribe(b),
      onPartial: (t) => { if (run.current === mine) setWords(t); },
      onLevel: (l) => {
        if (run.current !== mine) return;
        setLevel(l);
        const now = Date.now();
        if (now - lastBar.current > 60) { lastBar.current = now; setBars((b) => [...b, l].slice(-BARS)); }
      },
      onFinal: ({ text, language: said } = {}) => {
        if (run.current !== mine) return;
        stopClock(); setWords('');
        if (!text) { setError(errorText(props.current.language, 'not-heard')); to('idle'); return; }
        to('idle');
        props.current.haptic?.('send');
        props.current.onSend?.(text, said);
      },
      onError: (e) => {
        if (run.current !== mine) return;
        armed.current = false;
        stopClock();
        setError(errorText(props.current.language, e?.code));
        to('idle');
      },
      deps: deps.ears,
    });
    ears.current.ready?.catch?.(() => {});
  }, [deps, reset, to]);

  const begin = useCallback(() => {
    if (phaseRef.current !== 'idle') return;
    if (!armed.current) arm();
    if (!armed.current) return;
    armed.current = false;
    gate.current?.open();
    props.current.haptic?.('start');
    to('recording');
    clock.current = setInterval(() => setMs(Date.now() - startedAt.current), 100);
  }, [arm, to]);

  /** A press that was only a tap: the ears go, with no shake, no buzz and no token. */
  const disarm = useCallback(() => {
    if (!armed.current) return;
    armed.current = false;
    run.current += 1;
    gate.current?.shut(new Error('tap'));
    try { ears.current?.cancel(); } catch (_) { /* gone */ }
    ears.current = null;
    reset();
  }, [reset]);

  const discard = useCallback(() => {
    run.current += 1;
    stopClock();
    try { ears.current?.cancel(); } catch (_) { /* gone */ }
    ears.current = null;
  }, []);

  const cancel = useCallback(() => {
    if (phaseRef.current === 'idle' || phaseRef.current === 'sending') return;
    discard(); reset(); to('idle');
    props.current.haptic?.('cancel');
  }, [discard, reset, to]);

  const sendingTimer = useRef(null);
  const clearSendingTimer = () => { if (sendingTimer.current) { clearTimeout(sendingTimer.current); sendingTimer.current = null; } };
  useEffect(() => clearSendingTimer, []);
  const finishNow = useCallback(() => {
    to('sending');
    ears.current?.finish();
    clearSendingTimer();
    const mine = run.current;
    sendingTimer.current = setTimeout(() => {
      sendingTimer.current = null;
      if (run.current !== mine || phaseRef.current !== 'sending') return;
      run.current += 1;
      try { ears.current?.cancel(); } catch (_) { /* gone */ }
      ears.current = null;
      reset(); setError(errorText(props.current.language, 'failed')); to('idle');
    }, SENDING_TIMEOUT_MS);
  }, [reset, to]);

  const release = useCallback(() => {
    if (phaseRef.current !== 'recording') return; // a locked note ignores the finger lifting
    if (Date.now() - startedAt.current < MIN_NOTE_MS) {
      discard(); reset(); to('idle'); setShake((n) => n + 1);
      return;
    }
    finishNow();
  }, [discard, finishNow, reset, to]);

  const lock = useCallback(() => { if (phaseRef.current === 'recording') { to('locked'); props.current.haptic?.('lock'); } }, [to]);
  const send = useCallback(() => { if (phaseRef.current === 'locked') finishNow(); }, [finishNow]);

  return { phase, ms, level, bars, words, error, shake, arm, disarm, begin, release, lock, send, cancel, clearError: () => setError('') };
}
